import { PSEUDO_LOCALE } from './config'

// Resolution order for the language of a survey take/preview page — both the
// content AND the chrome around it (spec: survey-language-detection).
//
//   1. Explicit ?lang= URL param  (a shared link that pins a language always wins)
//   2. Respondent's picker choice (persisted per survey for the session/device)
//   3. Signed-in user's account locale
//   4. Browser language, narrowed to its base code
//   5. The survey's default language
//
// A language is only ever selected from the survey's available_languages; a
// rung whose candidate the survey doesn't offer is skipped, it does not stop
// the ladder. The one exception is the dev-only pseudo-locale (qps), honored
// from ?lang= so the hardcoded-string sweep can force marked chrome on a
// survey that naturally has no qps translation.

export interface SurveyLanguageInputs {
  urlLang?: string | null
  pickerChoice?: string | null
  userLocale?: string | null
  browserLang?: string | null
  availableLanguages: string[]
  defaultLanguage: string
  /** Pass import.meta.env.DEV at the call site; enables the qps bypass. */
  allowPseudo?: boolean
}

function narrow(lang: string | null | undefined): string | null {
  if (!lang) return null
  const base = lang.split('-')[0].toLowerCase()
  return base || null
}

export function resolveSurveyLanguage(inputs: SurveyLanguageInputs): string {
  if (inputs.allowPseudo && inputs.urlLang === PSEUDO_LOCALE) return PSEUDO_LOCALE

  const offered = (candidate: string | null | undefined): string | null => {
    const base = narrow(candidate)
    return base && inputs.availableLanguages.includes(base) ? base : null
  }

  return (
    offered(inputs.urlLang) ??
    offered(inputs.pickerChoice) ??
    offered(inputs.userLocale) ??
    offered(inputs.browserLang) ??
    inputs.defaultLanguage
  )
}

const PICKER_KEY_PREFIX = 'ip.surveyLang.'

/** The respondent's stored picker choice for one survey (device-local). */
export function getStoredSurveyLanguage(publicId: string): string | null {
  try {
    return window.localStorage.getItem(PICKER_KEY_PREFIX + publicId)
  } catch {
    return null
  }
}

export function storeSurveyLanguage(publicId: string, lang: string): void {
  try {
    window.localStorage.setItem(PICKER_KEY_PREFIX + publicId, lang)
  } catch {
    /* localStorage unavailable */
  }
}
